import { Button, Checkbox } from "@trafficsharkdsp/ui";
import { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";

import cls from "./GdprConsentBanner.module.css";

import {
    ALL_GDPR_CONSENT_SETTINGS,
    DEFAULT_GDPR_CONSENT_SETTINGS,
    ESSENTIAL_GDPR_CONSENT_SETTINGS,
    getGdprConsentSettings,
    saveGdprConsentSettings,
} from "@/shared/lib/gdprConsent";
import type { GdprConsentSettings } from "@/shared/lib/gdprConsent";

const CONSENT_OPTIONS: Array<{
    key: keyof Pick<GdprConsentSettings, "targetedAdvertising" | "personalization" | "analytics">;
    labelKey: string;
}> = [
    { key: "targetedAdvertising", labelKey: "common.gdpr-consent.targeted-advertising" },
    { key: "personalization", labelKey: "common.gdpr-consent.personalization" },
    { key: "analytics", labelKey: "common.gdpr-consent.analytics" },
];

export const GdprConsentBanner = () => {
    const { t } = useTranslation();
    const [isVisible, setIsVisible] = useState(false);
    const [settings, setSettings] = useState<GdprConsentSettings>(DEFAULT_GDPR_CONSENT_SETTINGS);

    useEffect(() => {
        const savedSettings = getGdprConsentSettings();

        if (savedSettings) {
            setSettings(savedSettings);
        }

        setIsVisible(!savedSettings);
    }, []);

    if (!isVisible) return null;

    const saveSettings = (nextSettings: GdprConsentSettings) => {
        saveGdprConsentSettings(nextSettings);
        setIsVisible(false);
    };

    const rejectNonEssential = () => saveSettings(ESSENTIAL_GDPR_CONSENT_SETTINGS);

    return (
        <section
            className={cls.banner}
            data-qa="cookie-banner"
            aria-label={t("common.gdpr-consent.title")}
        >
            <div className={cls.content}>
                <p className={cls.description}>
                    <Trans
                        i18nKey="common.gdpr-consent.description"
                        components={{
                            privacyLink: (
                                <a
                                    className={cls.link}
                                    href="https://appgrade.pro/privacy-policy"
                                    target="_blank"
                                    rel="noreferrer"
                                />
                            ),
                        }}
                    />
                </p>

                <span className={cls["settings-label"]}>
                    {t("common.gdpr-consent.data-storage-settings")}
                </span>

                <div className={cls.toggles}>
                    {CONSENT_OPTIONS.map(({ key, labelKey }) => (
                        <Checkbox
                            key={key}
                            className={cls.toggle}
                            checked={settings[key]}
                            onChange={() =>
                                setSettings((currentSettings) => ({
                                    ...currentSettings,
                                    [key]: !currentSettings[key],
                                }))
                            }
                        >
                            {t(labelKey)}
                        </Checkbox>
                    ))}
                </div>
            </div>

            <div className={cls.actions}>
                <Button
                    type="primary"
                    className={cls["action-button"]}
                    onClick={() => saveSettings(ALL_GDPR_CONSENT_SETTINGS)}
                >
                    {t("common.gdpr-consent.accept-all")}
                </Button>
                <Button
                    type="secondary"
                    className={cls["action-button"]}
                    buttonProps={{ "data-qa": "cookie-reject-button" }}
                    onClick={rejectNonEssential}
                >
                    {t("common.gdpr-consent.reject-non-essential")}
                </Button>
                <Button
                    type="secondary"
                    className={cls["action-button"]}
                    onClick={() => saveSettings(settings)}
                >
                    {t("common.gdpr-consent.save")}
                </Button>
            </div>

            <button
                className={cls["close-button"]}
                type="button"
                aria-label={t("common.gdpr-consent.close")}
                onClick={rejectNonEssential}
            >
                ×
            </button>
        </section>
    );
};
